import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CaretLeft as ChevronLeft, CaretRight as ChevronRight, GridFour as LayoutGrid } from '@phosphor-icons/react';
import { useCars } from '../context/CarsContext';
import { useDutyFree } from '../context/DutyFreeContext';
import { config } from '../config/runtimeConfig';
import { useTranslation } from '../i18n/I18nContext';
import LandingBody from '../components/landing/LandingBody';
import SalonHeader from '../components/landing/SalonHeader';
import { useDealerPreset } from '../config/dealerPresets';
import { useDealerScopeNavigate } from '../hooks/useDealerScopeNavigate';
import './CarLandingPage.css';

/**
 * Showroom mini-landing (issue #965). Owns the showroom chrome:
 *   - Close × FAB → /showroom catalog
 *   - Tinder-style swipe between cars in catalog order
 *   - Peek of the neighbour card behind the current one
 *   - Keyboard arrows (←/→ prev/next, Esc close) for desktop
 *
 * The actual landing visuals (hero + specs + CTA, or new-model layout)
 * live in <LandingBody> so we can render two of them — the active card
 * (front, interactive) and the peek (behind, scaled-down preview).
 *
 * @param {object} [props]
 * @param {string} [props.carId] — when rendered from MiniCarModal in
 *   widget mode (#878). Falls back to useParams().
 * @param {boolean} [props.isModal=false] — true inside MiniCarModal.
 *   Skips showroom chrome (modal owns its own close + viewport).
 */
const SWIPE_HINT_STORAGE_KEY = 'b2b-demo:landing-swipe-hint-seen';

const CarLandingPage = ({ carId: propCarId, isModal = false } = {}) => {
  const params = useParams();
  // Переносит dealer-scope параметры (?token) вперёд при свайпе / переходе в
  // каталог, чтобы reload не потерял дилера → «Car not found».
  const dealerNavigate = useDealerScopeNavigate();
  const { t } = useTranslation();

  // Swipe-hint affordance (#1022 review feedback). Bouncing chevron L+R
  // arrows on first visit signal "swipe to see other cars"; dismiss as soon
  // as user actually commits a swipe (handlePointerUp → setExit). Persist in
  // localStorage so returning users see a clean view.
  const [swipeHintsDismissed, setSwipeHintsDismissed] = useState(() => {
    try {
      return typeof window !== 'undefined'
        && window.localStorage?.getItem(SWIPE_HINT_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const dismissSwipeHints = useCallback(() => {
    setSwipeHintsDismissed((prev) => {
      if (prev) return prev;
      try { window.localStorage?.setItem(SWIPE_HINT_STORAGE_KEY, '1'); } catch {
        /* private mode / iframe restrictions — ignore */
      }
      return true;
    });
  }, []);
  const id = propCarId ?? params.id;
  const { getCarById, getAllCars, loading, error } = useCars();
  const { isDutyFree, calculateDutyFree } = useDutyFree();

  const car = getCarById(id);
  const allCars = getAllCars();

  // Autodis (new-model) dealer preset на car-landing — иначе тема страницы
  // (и виджета, который реактивно читает --theme-primary в App.jsx) остаётся
  // демо-дефолтной. Применяем только для new-model: страница общая, used-VIN
  // дилеры используют свою тему. Покрывает и MiniCarModal (рендерит CarLandingPage).
  useDealerPreset(car?.isNewModel ? 'autodis' : null);
  const currentIndex = useMemo(
    () => allCars.findIndex((c) => String(c.id) === String(id)),
    [allCars, id],
  );

  const goToShowroom = () => dealerNavigate('/showroom');
  // Логотип в шапке свайп-лендинга — только у Autodis. У витрины ShiftGears
  // логотип уже стоит плашкой над фото (BrandLogoPill), и второй в шапке
  // ложился на неё (#1441).
  const showSalonLogo = config.SHOWROOM_BRAND === 'autodis';
  const goToCarLanding = (carObj) => {
    if (!carObj) return;
    dealerNavigate(`/cars/${carObj.id}/landing`);
  };
  const goToPrev = () => {
    if (currentIndex < 0 || allCars.length < 2) return;
    const prevIdx = (currentIndex - 1 + allCars.length) % allCars.length;
    goToCarLanding(allCars[prevIdx]);
  };
  const goToNext = () => {
    if (currentIndex < 0 || allCars.length < 2) return;
    const nextIdx = (currentIndex + 1) % allCars.length;
    goToCarLanding(allCars[nextIdx]);
  };

  // Tinder-style swipe (#965). Algorithm matches react-tinder-card /
  // @use-gesture mechanics — without pulling in either library.
  //   1. Drag: translateX + rotate + minor scale-down; peek behind
  //      scales up + fades in over the same progress.
  //   2. Commit (release) uses BOTH distance and velocity:
  //      |dx| ≥ THRESHOLD  OR  |vx| ≥ VELOCITY → commit; else spring back.
  //   3. Exit: fly off-screen in the swiped direction; peek lands at
  //      full scale + opacity right as navigation fires.
  const SWIPE_THRESHOLD_PX = 80;
  const SWIPE_VELOCITY_COMMIT = 0.45;
  const SWIPE_ROTATE_FACTOR = 0.06;
  const SWIPE_MAX_ROTATE_DEG = 14;
  const SWIPE_MAX_SCALE_DELTA = 0.04;
  const SWIPE_VELOCITY_WINDOW_MS = 90;
  const SWIPE_EXIT_MS = 320;
  // Peek interpolation denominator — drag this many px to fully reveal
  // the neighbour. Lower number = peek snaps to full faster.
  const PEEK_FULL_AT_PX = 240;

  const swipeStartRef = useRef(null);
  const swipeSamplesRef = useRef([]);
  const horizontalLockedRef = useRef(false);
  const [dragX, setDragX] = useState(0);
  const [exit, setExit] = useState(null); // null | { dir, vx, viewportW }
  // True for one render after `id` changes — kills the front-card
  // transition so the new car doesn't visibly spring back from the
  // off-screen exit position of the previous swipe. The peek (which
  // was already showing the new car at center, full scale/opacity) just
  // "becomes" the front card without any animated movement.
  const [skipTransition, setSkipTransition] = useState(false);

  // Reset gesture state on car change so the next landing starts centered.
  useEffect(() => {
    setDragX(0);
    setExit(null);
    swipeStartRef.current = null;
    swipeSamplesRef.current = [];
    horizontalLockedRef.current = false;
    setSkipTransition(true);
    const raf = requestAnimationFrame(() => {
      // Clear the skip flag on the next frame so subsequent gestures
      // get their normal spring-back transition.
      setSkipTransition(false);
    });
    return () => cancelAnimationFrame(raf);
  }, [id]);

  const handlePointerDown = (e) => {
    if (isModal || exit) return;
    const now = performance.now();
    swipeStartRef.current = { x: e.clientX, y: e.clientY, t: now };
    swipeSamplesRef.current = [{ x: e.clientX, t: now }];
    horizontalLockedRef.current = false;
  };
  const handlePointerMove = (e) => {
    if (isModal || exit) return;
    const start = swipeStartRef.current;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (!horizontalLockedRef.current) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        swipeStartRef.current = null;
        return;
      }
      horizontalLockedRef.current = true;
    }
    const now = performance.now();
    const samples = swipeSamplesRef.current;
    samples.push({ x: e.clientX, t: now });
    while (samples.length > 1 && now - samples[0].t > SWIPE_VELOCITY_WINDOW_MS) {
      samples.shift();
    }
    setDragX(dx);
  };
  const computeVelocity = (endX, endT) => {
    const samples = swipeSamplesRef.current;
    if (samples.length === 0) return 0;
    const oldest = samples[0];
    const dt = endT - oldest.t;
    if (dt <= 0) return 0;
    return (endX - oldest.x) / dt;
  };
  const handlePointerUp = (e) => {
    if (isModal) return;
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    horizontalLockedRef.current = false;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const vx = computeVelocity(e.clientX, performance.now());
    swipeSamplesRef.current = [];

    const horizontalDominant = Math.abs(dx) >= Math.abs(dy);
    const distancePass = Math.abs(dx) >= SWIPE_THRESHOLD_PX;
    const velocityPass = Math.abs(vx) >= SWIPE_VELOCITY_COMMIT;
    if (!horizontalDominant || (!distancePass && !velocityPass)) {
      setDragX(0);
      return;
    }
    const dir = (Math.abs(dx) > 4 ? dx : vx) > 0 ? 'right' : 'left';
    setExit({ dir, vx, viewportW: window.innerWidth });
    // First successful commit — hint did its job, hide forever.
    dismissSwipeHints();
    setTimeout(() => {
      if (dir === 'right') goToPrev();
      else goToNext();
    }, SWIPE_EXIT_MS);
  };
  const handlePointerCancel = () => {
    swipeStartRef.current = null;
    swipeSamplesRef.current = [];
    horizontalLockedRef.current = false;
    setDragX(0);
  };

  // Swipe direction (left swipe → going to NEXT, right swipe → PREV).
  const swipeDir = useMemo(() => {
    if (exit) return exit.dir;
    if (dragX < 0) return 'left';
    if (dragX > 0) return 'right';
    return null;
  }, [dragX, exit]);

  // Neighbour car shown behind the active card. Derived from swipe direction:
  //   'left'  → user is going to NEXT (so peek next)
  //   'right' → user is going to PREV (so peek prev)
  // null when idle — peek is unmounted so we don't waste a second image fetch
  // on every visit.
  const peekCar = useMemo(() => {
    if (!swipeDir || allCars.length < 2 || currentIndex < 0) return null;
    if (swipeDir === 'left') {
      return allCars[(currentIndex + 1) % allCars.length];
    }
    return allCars[(currentIndex - 1 + allCars.length) % allCars.length];
  }, [swipeDir, currentIndex, allCars]);

  // Progress 0..1 from drag distance / exit state. Drives peek scale/opacity.
  const peekProgress = useMemo(() => {
    if (exit) return 1;
    if (!dragX) return 0;
    return Math.min(1, Math.abs(dragX) / PEEK_FULL_AT_PX);
  }, [dragX, exit]);

  // Front (active) card transform.
  // borderRadius rule (#965 feedback): rounded 8px while the card is being
  // dragged / exiting / freshly mounted from a swipe — drops to 0 at rest
  // so the centered card looks edge-to-edge. Spring-back transitions the
  // radius alongside the transform so the corners ease open, not snap.
  const swipeCardStyle = useMemo(() => {
    if (skipTransition) {
      return {
        transform: 'translateX(0) rotate(0deg) scale(1)',
        transition: 'none',
        borderRadius: '8px',
      };
    }
    if (exit) {
      const sign = exit.dir === 'right' ? 1 : -1;
      const boost = Math.min(280, Math.abs(exit.vx) * 200);
      const targetX = sign * (exit.viewportW * 1.2 + boost);
      const rot = Math.max(-25, Math.min(25, targetX * SWIPE_ROTATE_FACTOR * 0.6));
      return {
        transform: `translateX(${targetX}px) rotate(${rot}deg) scale(0.94)`,
        opacity: 0,
        transition:
          `transform ${SWIPE_EXIT_MS}ms cubic-bezier(0.32, 0.72, 0, 1),`
          + ` opacity ${SWIPE_EXIT_MS}ms ease-out,`
          + ` border-radius ${SWIPE_EXIT_MS}ms ease-out`,
        borderRadius: '8px',
      };
    }
    if (dragX !== 0) {
      const rot = Math.max(
        -SWIPE_MAX_ROTATE_DEG,
        Math.min(SWIPE_MAX_ROTATE_DEG, dragX * SWIPE_ROTATE_FACTOR),
      );
      const scale = 1 - Math.min(SWIPE_MAX_SCALE_DELTA, Math.abs(dragX) / 800);
      return {
        transform: `translateX(${dragX}px) rotate(${rot}deg) scale(${scale})`,
        transition: 'none',
        willChange: 'transform',
        borderRadius: '8px',
      };
    }
    return {
      transform: 'translateX(0) rotate(0deg) scale(1)',
      transition:
        'transform 380ms cubic-bezier(0.18, 0.89, 0.32, 1.28),'
        + ' border-radius 280ms ease-out',
      borderRadius: '0',
    };
  }, [dragX, exit, skipTransition]);

  // Peek (behind) card transform. Sits at 0.92 / 0.4 opacity when the front
  // card is centered, animates to full 1.0 / 1.0 as the front swipes off.
  const peekCardStyle = useMemo(() => {
    const scale = 0.92 + 0.08 * peekProgress;
    const opacity = 0.4 + 0.6 * peekProgress;
    let transition;
    if (exit) {
      transition =
        `transform ${SWIPE_EXIT_MS}ms cubic-bezier(0.32, 0.72, 0, 1),`
        + ` opacity ${SWIPE_EXIT_MS}ms ease-out`;
    } else if (dragX === 0) {
      // Released short — peek fades back out behind the spring-back front.
      transition = 'transform 380ms cubic-bezier(0.18, 0.89, 0.32, 1.28), opacity 380ms';
    } else {
      transition = 'none';
    }
    return {
      transform: `scale(${scale})`,
      opacity,
      transition,
      willChange: 'transform, opacity',
      // Peek card is always scaled-down behind the front, never fullscreen
      // at rest — keep the 8px rounding so its visible corners read as a
      // distinct card behind the active one (#965 feedback).
      borderRadius: '8px',
    };
  }, [peekProgress, dragX, exit]);

  // Keyboard arrows for desktop (a11y bonus — touch is the primary input).
  // Only when showroom flow is on: arrows navigate between landings, Esc
  // returns to /showroom. With the flag off there is no /showroom and no
  // swipe — leave keyboard untouched so the page behaves like a static
  // landing on those dealers.
  useEffect(() => {
    if (isModal || !config.ENABLE_SHOWROOM_CATALOG) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') goToPrev();
      else if (e.key === 'ArrowRight') goToNext();
      else if (e.key === 'Escape') goToShowroom();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModal, currentIndex, allCars.length]);

  // Hide the widget's collapsed bar while on the landing page. Modal owns
  // its own viewport, so this hack is unnecessary there.
  useEffect(() => {
    if (isModal) return undefined;
    document.body.classList.add('landing-active');
    return () => document.body.classList.remove('landing-active');
  }, [isModal]);

  useEffect(() => {
    console.debug('[CarLandingPage] Mounted with config:', {
      requestedId: id,
      apiUrl: config.CAR_API_URL,
      organizationId: config.ORGANIZATION_ID,
      inventorySlug: config.INVENTORY_SLUG,
      hasFrontendToken: !!config.FRONTEND_TOKEN,
      hasMountTheme: !!window.b2bMountParams?.theme,
    });
  }, [id]);

  // Opens the widget chat with car context. Identical to the previous
  // implementation — title / price derived locally for the launchContext.
  const handleOpenWidget = () => {
    try {
      const cleanMake = car?.make?.replace(/\*/g, '') || car?.make || '';
      const cleanModel = car?.model?.replace(/\*/g, '') || car?.model || '';
      const displayTitle = car?.name || `${cleanMake} ${cleanModel}`.trim();
      const displayPrice = car && isDutyFree ? calculateDutyFree(car) : car?.price;
      // #1362: `language` тут больше НЕ проставляем. Раньше сюда уходил язык
      // СТРАНИЦЫ (`useTranslation().language` = выбор ?? 'fr'), из-за чего
      // покупатель, не трогавший селектор, приезжал к агенту как «выбравший
      // французский» — и дефолт тенанта уже не мог сработать. Теперь язык
      // добавляет транспорт (`useTextChat`) и только если выбор действительно
      // был; см. `i18n/selectedLanguage.js`.
      const launchContext = {
        action: 'ask_about_car',
        workflow: 'showroom',
        metadata: {
          carId: car?.id,
          carTitle: displayTitle,
          price: displayPrice,
          make: car?.make,
          model: car?.model,
          year: car?.year,
        },
      };
      if (window.ShiftGearsWidget?.open) {
        window.ShiftGearsWidget.open('chat', launchContext);
      } else {
        window.dispatchEvent(
          new CustomEvent('shiftgears:open', { detail: { mode: 'chat', launchContext } }),
        );
      }
    } catch (err) {
      console.error('[CarLandingPage] Error opening widget:', err);
    }
  };

  if (loading) {
    return (
      <div className="landing__state" role="status">
        {t('salon.state.loading')}
      </div>
    );
  }
  // Сбой загрузки ≠ «такой машины нет». До #1034 (3c, C1) каталог Autodis
  // резолвился из бандла синхронно, и эта ветка была недостижима; теперь он
  // едет по сети, и без неё сетевая ошибка на QR-флоу (widget-view=mini-car)
  // читалась бы как «машина не найдена» — покупатель со сканом листовки видит
  // «нет машины» вместо «попробуйте ещё раз».
  if (error) {
    return (
      <div className="landing__state landing__state--error" role="alert">
        {t('salon.state.error')}
      </div>
    );
  }
  if (!car) {
    // `status`, не `alert`: «такой машины нет» — штатное состояние, а не сбой.
    // Ровно то различие empty ≠ failure, ради которого выше появилась error-ветка.
    return (
      <div className="landing__state" role="status">
        {t('salon.state.notFound')}
      </div>
    );
  }

  // Modal mode: skip showroom chrome — MiniCarModal owns close + viewport.
  // No swipe peek either; the modal is a single-card mini view.
  if (isModal) {
    return (
      <LandingBody car={car} onOpenWidget={handleOpenWidget} />
    );
  }

  // Showroom catalog flag OFF — pre-#965 behavior: just render the
  // landing fullscreen, no swipe shell, no peek, no close FAB. The
  // gesture/peek/exit state above still runs but produces no JSX —
  // hook order is preserved, the work is just unused. Dealers without
  // the showroom flow (Andys, ShiftGears) land here.
  if (!config.ENABLE_SHOWROOM_CATALOG) {
    return (
      <LandingBody car={car} onOpenWidget={handleOpenWidget} />
    );
  }

  return (
    <div
      className="landing-shell"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      {peekCar && (
        <div className="landing-peek" style={peekCardStyle} aria-hidden="true">
          <LandingBody car={peekCar} isInteractive={false} />
          {/* Header mirror on the peek card — keeps the SalonHeader (Autodis
              logo + lang + catalog) visible during the swipe handoff so it
              never blinks out between front-exit and new-front mount (#965).
              Inert: peek wrapper is aria-hidden + pointer-events: none. */}
          <div className="landing-header landing-header--peek">
            <SalonHeader
              brands={[]}
              showLanguage
              showLogo={showSalonLogo}
              actionsSlot={
                <span className="salon-header__catalog-btn" aria-hidden="true">
                  <LayoutGrid size={18} weight="regular" aria-hidden="true" />
                </span>
              }
            />
          </div>
        </div>
      )}
      {/* Swipe affordance hint (#1022): bouncing chevrons on left+right
          edges signal "you can swipe between cars". Anchored to .landing-shell
          (outside .landing-swipe-card) so they DON'T translate with the
          dragged card — they stay pinned to the viewport edges. Dismissed
          after the first committed swipe + persisted via localStorage so
          returning users see a clean landing. */}
      {!swipeHintsDismissed && (
        <>
          <span
            className="landing-swipe-hint landing-swipe-hint--left"
            aria-hidden="true"
          >
            <ChevronLeft size={32} weight="regular" />
          </span>
          <span
            className="landing-swipe-hint landing-swipe-hint--right"
            aria-hidden="true"
          >
            <ChevronRight size={32} weight="regular" />
          </span>
        </>
      )}
      <div className="landing-swipe-card" style={swipeCardStyle}>
        <LandingBody car={car} onOpenWidget={handleOpenWidget} />
        {/* Шапка — переиспользуем SalonHeader (как на Welcome): логотип
            Autodis слева + LanguageSelector + кнопка каталога справа (в
            actionsSlot, стиль = как у выбора языка). Лежит внутри swipe-card,
            поэтому едет вместе с картой — жест свайпа не меняется. Кнопка
            каталога ведёт в /showroom (#1022). */}
        <div className="landing-header">
          <SalonHeader
            brands={[]}
            showLanguage
            showLogo={showSalonLogo}
            actionsSlot={
              <button
                type="button"
                className="salon-header__catalog-btn"
                aria-label={t('landing.close.toCatalog')}
                onClick={goToShowroom}
              >
                <LayoutGrid size={18} weight="regular" aria-hidden="true" />
              </button>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default CarLandingPage;
